/* MJ pre-order promo — persistent teaser in front of the Klaviyo signup form.

   The 20% pre-order offer is a Klaviyo form (company WjASW8; W8GjDY desktop,
   Rk6tbC mobile) that hands over the code in exchange for an email. That trade is the
   point — it is what lets an abandoned cart be followed up — so nothing here ever
   reveals or applies the code without a signup. The teaser only opens Klaviyo's form.

   Why the teaser is ours and not Klaviyo's, measured on the live site:
     - Klaviyo's teaser does show first (display_order DISPLAY_BEFORE), but the form
       auto-opens after its delay and replaces it.
     - Dismissing the form sets Klaviyo's suppression cookie, which hides the whole
       form version — its teaser included. On every reload afterwards there was no
       form and no teaser at all. Klaviyo cannot express "show the form once, keep
       the teaser", which is the requirement.
     - `_klOnsite.push(['openForm', id])` still opens the form while that suppression
       is active. Verified. That is what makes a theme-owned entry point work.

   So: Klaviyo keeps the form, the copy and the code. This owns the persistent entry
   point, and adds the two things Klaviyo cannot do without sight of the cart —
   applying the code on signup, and standing down once the code is on the cart.

   Behaviour:
     - visible whenever the code is not on the cart
     - hidden once it is, back again if the shopper removes it
     - no dismiss button: Klaviyo's teaser has show_dismiss_button false, and an
       earlier collapse-to-tab version read as a second teaser underneath the first
     - Klaviyo's own teaser is suppressed so there is never a duplicate

   Markup: snippets/mj-klaviyo-promo.liquid. Styles: end of assets/main.css. */
(function () {
  "use strict";

  if (window.MJKlaviyoPromo) return;
  window.MJKlaviyoPromo = true;

  var KL_TEASER = '[class*="kl-teaser-"]';
  var MOBILE_MAX = 767;

  var config = window.MJPromo || {};
  var code = String(config.code || "").trim().toUpperCase();
  if (!code) return; // not configured — leave Klaviyo entirely alone

  var teaser = null;
  var codeOnCart = false;

  /* The ribbon exists only to open Klaviyo's form, so it must not outlive it. If the
     Klaviyo embed is ever switched off, `_klOnsite` never appears and the ribbon
     simply never shows, rather than sitting there as a button that does nothing.
     Klaviyo boots asynchronously, hence the poll rather than a single check.

     Note this cannot detect the narrower case of the embed still being installed but
     the *form* being deactivated in Klaviyo. For that, turn off "Enable the Klaviyo
     promo integration" in theme settings, or clear the discount code. */
  var klaviyoReady = false;
  var KLAVIYO_WAIT_MS = 10000;
  var KLAVIYO_POLL_MS = 300;

  function klaviyoPresent() {
    return !!(window._klOnsite && window.klaviyo);
  }

  function normalize(value) {
    return String(value == null ? "" : value).trim().toUpperCase();
  }

  /* Presence is tested by code, not by amount: this store's discount_codes entries
     carry no amount (see the note at assets/cart.js:1313).

     applicable:false is not automatically a rejection. On an empty cart Shopify marks
     every code inapplicable — there is nothing to discount yet — but it keeps the code
     and flips it to applicable:true the moment a line is added. A shopper who signs up
     before adding anything has still claimed the offer, so the ribbon must stand down;
     leaving it up invites them to claim again, which is what Bernard hit. On a cart
     that does have items, applicable:false means what it says and the ribbon stays. */
  function cartHasCode(cart) {
    if (!cart || !Array.isArray(cart.discount_codes)) return false;
    var cartIsEmpty = (cart.item_count || 0) === 0;
    for (var i = 0; i < cart.discount_codes.length; i++) {
      var entry = cart.discount_codes[i];
      if (!entry || normalize(entry.code) !== code) continue;
      if (entry.applicable !== false || cartIsEmpty) return true;
    }
    return false;
  }

  /* Klaviyo's own teaser would otherwise sit alongside ours for the few seconds
     before its form opens. Hidden rather than removed — it is Klaviyo's node and
     Klaviyo still reaches for it. */
  function hideKlaviyoTeaser() {
    try {
      var theirs = document.querySelectorAll(KL_TEASER);
      for (var i = 0; i < theirs.length; i++) {
        theirs[i].style.setProperty("display", "none", "important");
      }
    } catch (e) {}
  }

  function render() {
    if (!teaser) return;
    teaser.hidden = codeOnCart || !klaviyoReady;
  }

  function waitForKlaviyo() {
    var waited = 0;
    var poll = setInterval(function () {
      if (klaviyoPresent()) {
        clearInterval(poll);
        klaviyoReady = true;
        render();
        return;
      }
      waited += KLAVIYO_POLL_MS;
      if (waited >= KLAVIYO_WAIT_MS) {
        clearInterval(poll);
        // Klaviyo is not on this page — stay hidden rather than offer a dead button.
      }
    }, KLAVIYO_POLL_MS);
  }

  function onCart(cart) {
    codeOnCart = cartHasCode(cart);
    render();
  }

  function readCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (response) {
        return response.json();
      })
      .then(onCart)
      .catch(function () {
        onCart(null); // cart unreadable — err towards showing the offer
      });
  }

  function toast(type, message) {
    try {
      var theme = window.MinimogTheme;
      if (!theme || !theme.Notification || typeof theme.Notification.show !== "function") return;
      theme.Notification.show({
        target: document.body,
        method: "appendChild",
        type: type,
        message: message,
        last: 4000,
        sticky: true,
      });
    } catch (e) {}
  }

  function currentFormId() {
    return window.innerWidth <= MOBILE_MAX ? config.formMobile : config.formDesktop;
  }

  function pushOpen(formId) {
    // Two pushes: one alone is reliably ignored after a close, two sometimes work.
    window._klOnsite.push(["openForm", formId]);
    window._klOnsite.push(["openForm", formId]);
  }

  /* Hand over to Klaviyo. The email is captured there; we never shortcut it.

     The awkward part: Klaviyo refuses to re-open a form it closed earlier in the same
     page view, and that is the common path — its popup auto-opens, the shopper closes
     it, then clicks this. Measured on the live site, `openForm` in that state is
     ignored; pushing twice helps only intermittently, and passing an options object
     (allowReTriggering) throws inside Klaviyo. There is no reliable in-page route.

     What is reliable is that `openForm` always works on a fresh page view, including
     while Klaviyo's suppression cookie is set. So if the form has not reported itself
     open shortly after a click, we reload once and open it on the way back in. A
     reload is not elegant, but a dead claim button is worse, and this only happens on
     the path where Klaviyo has locked us out.

     Success is judged on Klaviyo's own `open` event, not on the DOM: its form node
     lingers mid-animation after a close, so a visibility check reports a form that is
     on its way out and the fallback never fires. That mistake is what made an earlier
     attempt at this look fixed when it was not. */
  var RELOAD_FLAG = "mj-promo-open-on-load";
  var openTimer = null;
  var sawOpen = false;

  function openKlaviyoForm() {
    var formId = currentFormId();
    if (!formId || !window._klOnsite) {
      console.error("MJ promo: Klaviyo is not available to open form", formId);
      return;
    }

    sawOpen = false;
    pushOpen(formId);

    clearTimeout(openTimer);
    openTimer = setTimeout(function () {
      if (sawOpen) return;
      try {
        sessionStorage.setItem(RELOAD_FLAG, "1");
        window.location.reload();
      } catch (e) {
        // No sessionStorage (private mode): nothing safe left to try, and reloading
        // without the flag would just drop the shopper back where they started.
      }
    }, 1500);
  }

  // Coming back from that reload: Klaviyo is in a clean state, so this opens.
  function openIfReturningFromReload() {
    var pending;
    try {
      pending = sessionStorage.getItem(RELOAD_FLAG) === "1";
      if (pending) sessionStorage.removeItem(RELOAD_FLAG); // one shot — never loop
    } catch (e) {
      return;
    }
    if (!pending) return;

    var formId = currentFormId();
    if (!formId || !window._klOnsite) return;
    // Klaviyo boots asynchronously; give it a moment before asking.
    setTimeout(function () {
      pushOpen(formId);
    }, 1200);
  }

  // Signup completed — put the code on the cart so the shopper does not have to.
  var applying = false;
  function applyAfterSignup() {
    if (applying || codeOnCart) return;
    if (!window.MJDiscounts || typeof window.MJDiscounts.applyCode !== "function") {
      console.error("MJ promo: cart.js has not exposed MJDiscounts; code not applied.");
      return;
    }

    applying = true;
    window.MJDiscounts
      .applyCode(code)
      .then(function (applied) {
        if (applied) toast("success", config.appliedText || "Discount applied to your cart");
        return readCart();
      })
      .catch(function (error) {
        // Klaviyo has still shown and emailed the code, so this is not fatal.
        console.error("MJ promo: applying the code failed:", error);
      })
      .then(function () {
        applying = false;
      });
  }

  function init() {
    teaser = document.getElementById("mj-promo-teaser");
    if (!teaser) return;

    teaser.addEventListener("click", openKlaviyoForm);

    // On an unpublished theme Shopify overlays a 68px preview bar across the bottom
    // of the viewport, which would sit on top of the teaser while Bernard tests it.
    if (document.querySelector("#PBarNextFrameWrapper, #PBarFrame, [id^='PBar']")) {
      teaser.classList.add("mj-promo-teaser--previewing");
    }

    window.addEventListener("klaviyoForms", function (event) {
      var type = event && event.detail && event.detail.type;
      if (type === "open") {
        sawOpen = true; // the form really did open — no reload needed
        clearTimeout(openTimer);
        return;
      }
      if (type === "submit" || type === "stepSubmit") applyAfterSignup();
    });

    openIfReturningFromReload();

    // Every cart mutation routed through the drawer, including the shopper removing
    // the code from the chips — that is what brings the teaser back.
    document.addEventListener("cart:updated", function (event) {
      if (event.detail && event.detail.cart) onCart(event.detail.cart);
    });

    // Needed on top of the event: a code applied in an earlier page view emits
    // nothing, so first paint has to ask the cart directly.
    readCart();
    waitForKlaviyo();

    // Klaviyo injects its teaser on its own schedule, so keep asserting.
    hideKlaviyoTeaser();
    if (typeof MutationObserver !== "undefined" && document.body) {
      var pending = null;
      new MutationObserver(function () {
        clearTimeout(pending);
        pending = setTimeout(hideKlaviyoTeaser, 120);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
